import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import {
  ActionDialogLayout,
  Button,
  Dialog,
  IconClose24,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@lightspeed/unified-components-helios-theme/react'
import { T, useT } from '@transifex/react'
import { OutletSelect } from '../../../sale-line-item/fulfilment/OutletSelect/OutletSelect'
import type { OutletOption } from '../../../sale-line-item/fulfilment/OutletSelect/OutletSelectTypes'
import { useSaleFulfilment } from '../../../sale-line-item/fulfilment/hook/useSaleFulfilment'
import type { ClfOversellLine } from './clf-oversell-guard'

export type ClfOversellOutletSelections = Record<
  string,
  {
    fulfilmentOutletId: string
  }
>

interface ClfOversellWarningModalProps {
  oversellLines: ClfOversellLine[]
  onClose: () => void
  onConfirm: (outletSelections: ClfOversellOutletSelections) => void
}

interface ClfOversellOutletSelectProps {
  oversellLine: ClfOversellLine
  selectedOutletId?: string
  onOutletChange: (outlet: OutletOption) => void
  portalTargetRef: React.RefObject<HTMLDivElement>
}

// Per-row component because useSaleFulfilment is keyed by line item, and hooks
// can't be called inside the oversell rows map.
const ClfOversellOutletSelect: FunctionComponent<ClfOversellOutletSelectProps> = ({
  oversellLine,
  selectedOutletId,
  onOutletChange,
  portalTargetRef,
}) => {
  const { isFulfilmentOutletDisabled } = useSaleFulfilment(
    oversellLine.lineItemLocalId
  )

  return (
    <OutletSelect
      disabled={isFulfilmentOutletDisabled}
      includeAvailability
      initialSelectedOutletId={selectedOutletId}
      onOutletChange={onOutletChange}
      portalTargetRef={portalTargetRef}
      productId={oversellLine.productId}
    />
  )
}

export const ClfOversellWarningModal: FunctionComponent<ClfOversellWarningModalProps> = ({
  oversellLines,
  onClose,
  onConfirm,
}) => {
  const t = useT()
  const portalTargetRef = useRef<HTMLDivElement | null>(null)
  const dropdownPortalTargetRef = useRef<HTMLDivElement | null>(null)
  const [portalTargetReady, setPortalTargetReady] = useState(false)
  const [dropdownPortalTargetReady, setDropdownPortalTargetReady] = useState(
    false
  )
  const [
    outletSelections,
    setOutletSelections,
  ] = useState<ClfOversellOutletSelections>(() =>
    oversellLines.reduce<ClfOversellOutletSelections>(
      (selections, oversellLine) => ({
        ...selections,
        [oversellLine.lineItemLocalId]: {
          fulfilmentOutletId: oversellLine.fulfilmentOutletId,
        },
      }),
      {}
    )
  )

  useEffect(() => {
    const portalTarget = document.createElement('div')
    portalTarget.className = 'uc-base'
    document.body.appendChild(portalTarget)
    portalTargetRef.current = portalTarget

    setPortalTargetReady(true)

    return () => {
      portalTargetRef.current = null
      portalTarget.remove()
    }
  }, [])

  useEffect(() => {
    if (portalTargetReady && dropdownPortalTargetRef.current) {
      setDropdownPortalTargetReady(true)
    }
  }, [portalTargetReady])

  if (!portalTargetReady) {
    return null
  }

  const handleOutletChange = (
    lineItemLocalId: string,
    outlet: OutletOption
  ) => {
    setOutletSelections(currentSelections => ({
      ...currentSelections,
      [lineItemLocalId]: {
        fulfilmentOutletId: outlet.id,
      },
    }))
  }

  return (
    <Dialog
      opened
      onClose={onClose}
      role="alertdialog"
      size="medium"
      ariaLabel={t('Some items exceed available stock', {
        _context: 'Modal aria label',
      })}
      portalTargetRef={portalTargetRef}
      closeControlSlot={<IconClose24 />}
    >
      <div className="font-general">
        <div ref={dropdownPortalTargetRef} className="uc-base" />
        <ActionDialogLayout
          titleSlot={
            <T
              _str="Not enough stock at selected outlet"
              _context="Modal title"
            />
          }
          subtitleSlot={
            <T
              _str="The selected outlet doesn't have enough stock to fulfill the sale. Select a different outlet with sufficient stock, or go back to split the item quantity across outlets. Continuing anyway will create a special order for the shortfall."
              _context="Modal body"
            />
          }
          footerSlot={
            <div className="flex justify-end gap-2">
              <Button appearance="secondary" onClick={onClose}>
                <T _str="Back" _context="Back from CLF oversell warning" />
              </Button>
              <Button
                appearance="primary"
                onClick={() => onConfirm(outletSelections)}
              >
                <T _str="Continue" _context="Button text" />
              </Button>
            </div>
          }
        >
          <Table
            ariaLabel={t(
              'Items that exceed selected fulfillment outlet stock',
              { _context: 'Table aria label' }
            )}
            customClasses={{ container: 'table-fixed' }}
          >
            <TableHead>
              <TableRow>
                <TableHeadCell
                  align="start"
                  customClasses={{ headCell: 'w-2/5' }}
                >
                  <T _str="Item" _context="Table column header" />
                </TableHeadCell>
                <TableHeadCell
                  align="center"
                  customClasses={{ headCell: 'w-[10%]' }}
                >
                  <T _str="Qty" _context="Table column header" />
                </TableHeadCell>
                <TableHeadCell
                  align="start"
                  customClasses={{ headCell: 'w-1/2' }}
                >
                  <T _str="Fulfillment outlet" _context="Table column header" />
                </TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {oversellLines.map(oversellLine => (
                <TableRow
                  key={oversellLine.lineItemLocalId}
                  rowKey={oversellLine.lineItemLocalId}
                >
                  <TableCell align="start" customClasses={{ cell: 'w-2/5' }}>
                    {oversellLine.productName}
                  </TableCell>
                  <TableCell align="center" customClasses={{ cell: 'w-[10%]' }}>
                    {oversellLine.quantity}
                  </TableCell>
                  <TableCell
                    align="start"
                    customClasses={{ cell: 'w-1/2 min-w-0' }}
                  >
                    {dropdownPortalTargetReady && (
                      <ClfOversellOutletSelect
                        oversellLine={oversellLine}
                        selectedOutletId={
                          outletSelections[oversellLine.lineItemLocalId]
                            ?.fulfilmentOutletId
                        }
                        onOutletChange={outlet =>
                          handleOutletChange(
                            oversellLine.lineItemLocalId,
                            outlet
                          )
                        }
                        portalTargetRef={dropdownPortalTargetRef}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ActionDialogLayout>
      </div>
    </Dialog>
  )
}
